import React, { useState, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { GET_PASSAGE_PLANS, GET_KINDS } from '../graphql/queries';
import ShipCard from './ShipCard';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import './ShipList.css';

const DEFAULT_SHIP_ICON = '/static/default-ship-icon.png';
const ALL_SHIPS_ICON = '/static/all-ships-icon.png';

const ShipList = () => {
  const [selectedDirection, setSelectedDirection] = useState('all'); // 'all', 'north', 'south'
  const [selectedDate, setSelectedDate] = useState(null); // null = 今日（デフォルト）、または 'YYYY-MM-DD' 形式
  const [selectedKindId, setSelectedKindId] = useState(null); // null = 全船種
  const [isRefetchingKind, setIsRefetchingKind] = useState(false);
  const navigate = useNavigate();

  const { data: kindsData } = useQuery(GET_KINDS, {
    variables: { nameContains: '' },
  });
  const kinds = kindsData?.getKinds || [];

  // ローカル時間でYYYY-MM-DD形式の日付文字列を取得
  const getLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 日付文字列からDateオブジェクトを作成（ローカル時間）
  const parseLocalDate = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // 日付を1日進める
  const handleNextDay = async () => {
    const currentDate = selectedDate
      ? parseLocalDate(selectedDate)
      : new Date();
    const nextDate = new Date(currentDate);
    nextDate.setDate(currentDate.getDate() + 1);
    const nextDateString = getLocalDateString(nextDate);
    setSelectedDate(nextDateString);
    // クエリを再実行
    await refetch({
      startAt: nextDateString,
      endAt: nextDateString,
      kindId: selectedKindId || null,
    });
  };

  // 日付を1日戻す
  const handlePreviousDay = async () => {
    const currentDate = selectedDate
      ? parseLocalDate(selectedDate)
      : new Date();
    const prevDate = new Date(currentDate);
    prevDate.setDate(currentDate.getDate() - 1);
    const prevDateString = getLocalDateString(prevDate);
    setSelectedDate(prevDateString);
    // クエリを再実行
    await refetch({
      startAt: prevDateString,
      endAt: prevDateString,
      kindId: selectedKindId || null,
    });
  };

  // 今日に戻る
  const handleToday = async () => {
    setSelectedDate(null);
    // 明示的にrefetchを呼び出してクエリを再実行
    await refetch({
      startAt: null,
      endAt: null,
      kindId: selectedKindId || null,
    });
  };

  // 選択された日付を表示用にフォーマット
  const getDisplayDate = () => {
    if (!selectedDate) {
      return getLocalDateString(new Date());
    }
    return selectedDate;
  };

  // 今日かどうかを判定
  const isToday = () => {
    if (!selectedDate) return true;
    return selectedDate === getLocalDateString(new Date());
  };

  // 船種フィルタ切り替え時は即時に refetch して表示を更新
  const handleKindFilterClick = (kindId) => {
    const newKindId = kindId === selectedKindId ? null : kindId;
    setSelectedKindId(newKindId);
    setIsRefetchingKind(true);
    refetch({
      startAt: selectedDate || null,
      endAt: selectedDate || null,
      kindId: newKindId || null,
    }).finally(() => setIsRefetchingKind(false));
  };

  const queryVariables = useMemo(() => ({
    startAt: selectedDate || null,
    endAt: selectedDate || null,
    kindId: selectedKindId || null,
  }), [selectedDate, selectedKindId]);

  const { loading, error, data, refetch } = useQuery(GET_PASSAGE_PLANS, {
    variables: queryVariables,
    pollInterval: 30000, // 30秒ごとに自動更新
  });

  const handleRefresh = () => {
    refetch();
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage error={error} />;

  const allPassagePlans = data?.getPassagePlans?.passagePlans || [];
  const lastUpdated = data?.getPassagePlans?.lastUpdatedAt;
  const fetchedAt = data?.getPassagePlans?.fetchedAt;

  // 方向でフィルタリング
  const passagePlans = selectedDirection === 'all'
    ? allPassagePlans
    : allPassagePlans.filter(plan => plan.direction === selectedDirection);

  return (
    <div className="ship-list">
      <div className="ship-list-header">
        <h2>船舶航行情報</h2>
        <div className="ship-list-controls">
          <button
            className="search-button"
            onClick={() => navigate('/passage-plan-search')}
          >
            📅 日付検索
          </button>
          <button
            className="search-button"
            onClick={() => navigate('/search')}
          >
            🔍 船舶検索
          </button>
          <button
            className="refresh-button"
            onClick={handleRefresh}
            disabled={loading}
          >
            🔄 更新
          </button>
        </div>
      </div>

      <div className="date-navigation">
        <button
          className="date-nav-button"
          onClick={handlePreviousDay}
          title="前日"
        >
          ← 前日
        </button>
        <button
          className={`date-nav-button date-nav-today ${isToday() ? 'active' : ''}`}
          onClick={handleToday}
          title="今日に戻る"
        >
          📅 {getDisplayDate()}
        </button>
        <button
          className="date-nav-button"
          onClick={handleNextDay}
          title="翌日"
        >
          翌日 →
        </button>
      </div>

      <div className="direction-tabs">
        <button
          className={`direction-tab ${selectedDirection === 'all' ? 'active' : ''}`}
          onClick={() => setSelectedDirection('all')}
        >
          全て
        </button>
        <button
          className={`direction-tab ${selectedDirection === 'north' ? 'active' : ''}`}
          onClick={() => setSelectedDirection('north')}
        >
          北航船
        </button>
        <button
          className={`direction-tab ${selectedDirection === 'south' ? 'active' : ''}`}
          onClick={() => setSelectedDirection('south')}
        >
          南航船
        </button>
      </div>

      <div className="kind-filter">
        <button
          className={`kind-filter-item kind-filter-all ${selectedKindId === null ? 'active' : ''}`}
          onClick={() => handleKindFilterClick(null)}
          title="全船種"
          disabled={isRefetchingKind}
        >
          <img
            src={ALL_SHIPS_ICON}
            alt=""
            className="kind-filter-icon"
          />
        </button>
        {kinds.map((kind) => (
          <button
            key={kind.id}
            className={`kind-filter-item ${selectedKindId === kind.id ? 'active' : ''}`}
            onClick={() => handleKindFilterClick(kind.id)}
            title={kind.name || '船種'}
            disabled={isRefetchingKind}
          >
            <img
              src={kind.iconUrl || DEFAULT_SHIP_ICON}
              alt=""
              className="kind-filter-icon"
              onError={(e) => { e.target.src = DEFAULT_SHIP_ICON; }}
            />
          </button>
        ))}
      </div>

      {(lastUpdated || fetchedAt) && (
        <div className="update-info">
          {lastUpdated && (
            <p>
              更新: {new Date(lastUpdated).toLocaleString('ja-JP')}
            </p>
          )}
          {fetchedAt && (
            <p>
              取得: {new Date(fetchedAt).toLocaleString('ja-JP')}
            </p>
          )}
        </div>
      )}

      <div className={`ship-grid ${isRefetchingKind ? 'ship-grid-refetching' : ''}`}>
        {passagePlans.length === 0 ? (
          <div className="no-data">
            <p>該当する航行情報がありません</p>
          </div>
        ) : (
          passagePlans.map((passagePlan) => (
            <ShipCard key={passagePlan.id} passagePlan={passagePlan} />
          ))
        )}
      </div>
    </div>
  );
};

export default ShipList;
